import { loadFromFile } from "@pictograms/cad/formats/index"
import { createLogger } from "@pictograms/utils/logger"
import { CadWorkerClient } from "@pictograms/cad/worker/CadWorkerClient"
import { sanitizeCadJson } from "../helpers/sanitize-cad-json.helper"

const logger = createLogger('cad-file.service')

// Worker singleton para el parseo (off main thread). El Worker es lazy dentro
// de CadWorkerClient (se crea en la primera llamada).
let _parseWorker = null
function getParseWorker() {
  if (!_parseWorker) _parseWorker = new CadWorkerClient()
  return _parseWorker
}

/**
 * Parsea un archivo CAD (DXF/DWG) y devuelve el cadJson saneado.
 *
 * El parse corre EN EL WORKER (off main thread) → el hilo principal no se
 * congela y el loader puede repintar la fase actual. Si el worker falla (bundle
 * no compilado, error…), cae a un parse en el main thread (comportamiento previo).
 * El saneamiento (Filtro 1) se aplica en main (barato).
 *
 * @param {File}    file
 * @param {Object}  opts
 * @param {Function} opts.onProgress - (phase) => void   phase: 'reading' | 'parsing'
 * @returns {Promise<Object>} cadJson parseado y saneado
 * @throws {Error} Si el parse falla (worker y fallback). Mensaje apto para el usuario.
 */
export async function parseCadFile(file, { onProgress } = {}) {
  try {
    let dxf
    try {
      // onProgress recibe { phase, percent? } del worker (reading / parsing+%).
      dxf = await getParseWorker().ParseDxf(file, (p) => onProgress?.(p))
    } catch (werr) {
      // Fallback a main thread (p. ej. worker no disponible). Bloquea el hilo,
      // pero garantiza que la carga funciona (sin % de parse).
      logger.warn('worker parse falló, fallback a main thread:', werr?.message)
      onProgress?.({ phase: 'parsing' })
      dxf = await loadFromFile(file, {})
    }

    const rawCount = dxf?.entities?.length ?? 0
    // Saneamiento: descarta polilíneas con vertex en (0,0) (artefactos
    // XREF/INSERT que crean "líneas hacia el origen"). El encuadre lo garantiza
    // el render (fix OCS en DxfScene.GetInsertionTransform), no un recorte.
    const { cadJson: clean, removed } = sanitizeCadJson(dxf)
    const sanitizedNote = removed > 0 ? ` (sanitized: -${removed} en origen)` : ''
    logger.info(`parsed cadJson — entities: ${rawCount}${sanitizedNote}`)
    return clean
  } catch (err) {
    logger.error('parse failed:', err)
    throw err   // re-throw para que el caller decida UI
  }
}
